import axios from "axios";

axios.defaults.withCredentials = true;

const isBrowser = typeof window !== "undefined";
const normalizeBase = (value) => {
  if (!value) return "";
  return String(value).replace(/\/$/, "");
};

const isReactDevServer = isBrowser && ["3000", "3001"].includes(window.location.port);
const sameHostHttpBase = (port) => {
  if (!isBrowser) return "";
  return window.location.protocol + "//" + window.location.hostname + ":" + port;
};

const defaultApiBase = isReactDevServer ? sameHostHttpBase("8000") : "";
export const API_BASE = normalizeBase(
  process.env.REACT_APP_API_BASE || defaultApiBase
);

export const CHAT_ENABLED = String(
  process.env.REACT_APP_CHAT_ENABLED ?? "true"
).toLowerCase() === "true";

const wsBaseFromHttpBase = (base) => {
  if (!base && isBrowser) {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}`;
  }
  return String(base || "").replace(/^http:/, "ws:").replace(/^https:/, "wss:");
};

export const CHAT_WS_URL = normalizeBase(
  process.env.REACT_APP_CHAT_WS_URL || `${wsBaseFromHttpBase(API_BASE)}/chat/ws`
);

const buildApiUrl = (base, path) => `${base}${path}`;


async function fetchJsonOrThrow(url, requestOptions = {}) {
  const response = await fetch(url, { credentials: "include", ...requestOptions });
  const contentType = response.headers.get("content-type") || "";
  const body = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    const detail = typeof body === "object" && body !== null ? body.detail : body;
    throw new Error(detail || `Request failed with status ${response.status}`);
  }

  if (!contentType.includes("application/json")) {
    throw new Error(`Expected JSON response from ${url}`);
  }

  return body;
}

export async function fetchOutcomeOptions() {
  return fetchJsonOrThrow(buildApiUrl(API_BASE, "/outcomes/options"));
}

export async function fetchSuicideOutcomes(filters = {}) {
  const params = new URLSearchParams();
  params.set("breakdown_type", filters.breakdown_type || filters.breakdownType || "overall");
  params.set("gender", filters.gender || "All");
  params.set("race", filters.race || "All");
  params.set("cause", filters.cause || "All");
  params.set("education", filters.education || "All");
  params.set("place_of_death", filters.place_of_death || filters.placeOfDeath || "All");
  if (filters.year) params.set("year", filters.year);

  return fetchJsonOrThrow(buildApiUrl(API_BASE, `/outcomes/suicide?${params.toString()}`));
}

export async function fetchOutcomeBreakdown(filters = {}) {
  const params = new URLSearchParams();
  params.set("year", filters.year);
  params.set("layer", filters.layer || "count");
  params.set("breakdown_type", filters.breakdown_type || filters.breakdownType || "gender");
  const states = Array.isArray(filters.states) ? filters.states.filter(Boolean).join(",") : filters.states;
  if (states) params.set("states", states);

  return fetchJsonOrThrow(buildApiUrl(API_BASE, `/outcomes/breakdown?${params.toString()}`));
}

export async function fetch988Indicator(filters = {}) {
  const params = new URLSearchParams();
  params.set("metric", filters.metric || "in_state_answer_rate");
  if (filters.year) params.set("year", filters.year);

  return fetchJsonOrThrow(buildApiUrl(API_BASE, `/indicators/988?${params.toString()}`));
}

export async function fetch988Comparison(filters = {}) {
  const params = new URLSearchParams();
  if (filters.year) params.set("year", filters.year);
  params.set("metric", filters.metric || "in_state_answer_rate");
  params.set("include_national", filters.include_national ?? filters.includeNational ?? true ? "true" : "false");
  const states = Array.isArray(filters.states) ? filters.states.filter(Boolean).join(",") : filters.states;
  if (states) params.set("states", states);

  return fetchJsonOrThrow(buildApiUrl(API_BASE, `/indicators/988/comparison?${params.toString()}`));
}

export async function fetch988Trend(filters = {}) {
  const params = new URLSearchParams();
  params.set("metric", filters.metric || "in_state_answer_rate");
  params.set("include_national", filters.include_national ?? filters.includeNational ?? true ? "true" : "false");
  params.set("granularity", filters.granularity || "yearly");
  const states = Array.isArray(filters.states) ? filters.states.filter(Boolean).join(",") : filters.states;
  if (states) params.set("states", states);

  return fetchJsonOrThrow(buildApiUrl(API_BASE, `/indicators/988/trend?${params.toString()}`));
}

export async function fetchNsduhTreatmentSeries() {
  return fetchJsonOrThrow(
    buildApiUrl(API_BASE, "/indicators/nsduh-treatment"),
    { cache: "no-store" }
  );
}


export async function fetchOutcomeTrend(filters = {}) {
  const params = new URLSearchParams();
  params.set("layer", filters.layer || "count");
  params.set("breakdown_type", filters.breakdown_type || filters.breakdownType || "overall");
  params.set("include_national", filters.include_national ?? filters.includeNational ?? true ? "true" : "false");
  const states = Array.isArray(filters.states) ? filters.states.filter(Boolean).join(",") : filters.states;
  if (states) params.set("states", states);

  ["gender", "race", "cause", "education", "place_of_death"].forEach((key) => {
    const camelKey = key === "place_of_death" ? "placeOfDeath" : key;
    const value = filters[key] ?? filters[camelKey];
    if (Array.isArray(value)) {
      value.filter(Boolean).forEach((selected) => params.append(key, selected));
    } else if (value) {
      params.set(key, value);
    }
  });

  return fetchJsonOrThrow(buildApiUrl(API_BASE, `/outcomes/trend?${params.toString()}`));
}


export async function loginUser({ email, password }) {
  const form = new URLSearchParams();
  form.set("username", email);
  form.set("password", password);

  await axios.post(buildApiUrl(API_BASE, "/auth/cookie/login"), form, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    withCredentials: true,
  });

  return fetchCurrentUser();
}

export async function logoutUser() {
  await axios.post(buildApiUrl(API_BASE, "/auth/cookie/logout"), null, {
    withCredentials: true,
  });
}

export async function fetchCurrentUser() {
  const response = await axios.get(buildApiUrl(API_BASE, "/users/me"), {
    withCredentials: true,
  });
  return response.data;
}

export async function fetchChatSession() {
  const response = await axios.get(buildApiUrl(API_BASE, "/chat/session"), {
    withCredentials: true,
  });
  return response.data;
}

export async function clearChatSession(sessionId) {
  const response = await axios.delete(buildApiUrl(API_BASE, "/chat/session"), {
    params: sessionId ? { session_id: sessionId } : {},
    withCredentials: true,
  });
  return response.data;
}
